import React, { useState, useRef, useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import styles from './Navbar.module.css';

interface NavbarProps {
  menuName: string;
  isDarkMode: boolean;
  onToggleDarkMode: () => void;
  onNavigateHome: () => void;
  userProfile?: {
    name: string;
    email: string;
    avatar?: string;
  };
  onSignOut?: () => void;
  onProfileClick?: () => void;
}

const Navbar: React.FC<NavbarProps> = ({
  menuName,
  isDarkMode,
  onToggleDarkMode,
  onNavigateHome,
  userProfile,
  onSignOut,
  onProfileClick,
}) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  const handleProfileClick = () => {
    setIsDropdownOpen(false);
    onProfileClick?.();
  };

  const handleSignOut = () => {
    setIsDropdownOpen(false);
    onSignOut?.();
  };
  return (
    <nav className={`${styles.navbar} ${isDarkMode ? styles.navbarDark : styles.navbarLight}`}>
      <div className={styles.mainHeader}>
        <h3 className={styles.navTitle} style={{ color: '#193e6e' }}>
          {menuName}
        </h3>
        
        <div className={styles.navbarCollapse}>
          <ul className={styles.navbarNav}>
            <li className={styles.navItem}>
              <button
                className={styles.navButton}
                style={{ fontSize: '13px' }}
                onClick={onNavigateHome}
              >
                Back To Home
              </button>
            </li>
            
            <li className={styles.navItem}>
              <div className={styles.themeToggle} onClick={onToggleDarkMode}>
                <div className={styles.themeToggleContainer}>
                  <div className={isDarkMode ? styles.themeToggleCheck : styles.themeToggleUncheck}>
                    <span>{isDarkMode ? '☀️' : '🌙'}</span>
                  </div>
                </div>
                <div
                  className={styles.themeToggleCircle}
                  style={{
                    left: isDarkMode ? '27px' : '1px',
                    backgroundColor: isDarkMode ? '#4d4d4d' : '#fafafa',
                    borderColor: isDarkMode ? '#fafafa' : '#4d4d4d',
                  }}
                />
                <input
                  className={styles.themeToggleInput}
                  type="checkbox"
                  aria-label="Toggle Button"
                  checked={isDarkMode}
                  readOnly
                />
              </div>
            </li>
            
            {userProfile && (
              <li className={styles.navItem}>
                <div className={styles.dropdown} ref={dropdownRef}>
                  <button className={styles.dropdownToggle} onClick={toggleDropdown}>
                    <img
                      src={userProfile.avatar || '/default-avatar.png'}
                      alt="Profile"
                      className={`${styles.avatar} ${styles.avatarOnline}`}
                    />
                  </button>
                  
                  <div className={`${styles.dropdownMenu} ${isDropdownOpen ? styles.dropdownMenuOpen : ''}`}>
                    <div className={styles.profileInfo}>
                      <img
                        src={userProfile.avatar || '/default-avatar.png'}
                        alt="Profile"
                        className={styles.profileImg}
                      />
                      <div>
                        <div className={styles.profileName}>{userProfile.name}</div>
                        <div className={styles.profileEmail}>{userProfile.email}</div>
                      </div>
                    </div>
                    
                    <div className={styles.dropdownDivider}></div>
                    
                    <ul className={styles.navListUnstyled}>
                      <li className={styles.navItem}>
                        <NavLink
                          className={styles.dropdownItem}
                          to="/profile/about"
                          onClick={handleProfileClick}
                        >
                          <span className={styles.mrSpacing}>👤</span>
                          Profile
                        </NavLink>
                      </li>
                      <li className={styles.navItem}>
                        <button className={styles.dropdownItem} onClick={handleSignOut}>
                          <span className={styles.mrSpacing}>🚪</span>
                          Sign Out
                        </button>
                      </li>
                    </ul>
                  </div>
                </div>
              </li>
            )}
          </ul>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
